<script setup>
defineOptions({ name: "SegmentAutopecasPage" });
import { onMounted, onUnmounted, ref, defineAsyncComponent } from "vue";
import {
  Cog6ToothIcon,
  BanknotesIcon,
  ChatBubbleLeftRightIcon,
  ClipboardDocumentListIcon,
  ArrowPathIcon,
  ScaleIcon,
  DocumentCheckIcon,
} from "@heroicons/vue/24/outline";
import {
  Cog6ToothIcon as Cog6ToothIconSolid,
  WrenchScrewdriverIcon,
  BoltIcon,
  BeakerIcon,
  SpeakerWaveIcon,
  WrenchIcon,
} from "@heroicons/vue/24/solid";
import { useSeo } from "@/composables/useSeo";
import FaqPanel from "@/components/ui/FAQ.vue";
import SegmentHero from "@/components/ui/SegmentHero.vue";
import imageBg from "@/assets/segments/autopecas.webp";
import visaoGeralImg from "@/assets/visaogeralautopecas.webp";
import gestaoTributaria from "@/assets/gestao-tributaria.webp";

const Clients = defineAsyncComponent(() => import("@/components/home/Clients.vue"));
const CTA = defineAsyncComponent(() => import("@/components/home/CTA.vue"));

// Revela o elemento com fade+slide assim que ele entra na viewport ao rolar a página.
const vReveal = {
  mounted(el) {
    el.classList.add("reveal-init");
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add("reveal-visible");
          io.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" },
    );
    io.observe(el);
  },
};

// ---- Navegação fixa (mesma mecânica das páginas de solução/segmento) ----
const navLinks = [
  { label: "Visão geral", href: "#visao-geral" },
  { label: "Desafios", href: "#desafios" },
  { label: "Categorias", href: "#categorias" },
  { label: "Funcionalidades", href: "#funcionalidades" },
  { label: "Fiscal", href: "#fiscal" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Dúvidas", href: "#duvidas" },
];

const activeNav = ref(navLinks[0]?.href ?? "#visao-geral");
const setActiveNav = (href) => {
  activeNav.value = href;
};

const topOffsetPx = 140;
let scrollRaf = null;
const updateActiveFromScroll = () => {
  let current = navLinks[0]?.href ?? "#visao-geral";
  for (const link of navLinks) {
    const sectionEl = document.querySelector(link.href);
    if (!sectionEl) continue;
    const top = sectionEl.getBoundingClientRect().top;
    if (top - topOffsetPx <= 0) {
      current = link.href;
    } else {
      break;
    }
  }
  if (current !== activeNav.value) {
    setActiveNav(current);
  }
};

const onScroll = () => {
  if (scrollRaf) return;
  scrollRaf = requestAnimationFrame(() => {
    scrollRaf = null;
    updateActiveFromScroll();
  });
};

const openConsultorModal = () => {
  window.dispatchEvent(new CustomEvent("open-consultor-modal"));
};

const handleNavClick = (event, href) => {
  event.preventDefault();
  setActiveNav(href);
  history.replaceState(null, "", href);
  const el = document.querySelector(href);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  }
};

const syncActiveNavFromHash = () => {
  const hash = window.location.hash;
  if (!hash) return;
  if (navLinks.some((l) => l.href === hash)) {
    setActiveNav(hash);
  }
};

onMounted(() => {
  syncActiveNavFromHash();
  window.addEventListener("hashchange", syncActiveNavFromHash, { passive: true });
  window.addEventListener("scroll", onScroll, { passive: true });
  updateActiveFromScroll();
});

onUnmounted(() => {
  window.removeEventListener("hashchange", syncActiveNavFromHash);
  window.removeEventListener("scroll", onScroll);
  if (scrollRaf) cancelAnimationFrame(scrollRaf);
});

// Desafios reais de quem toca uma loja de autopeças/oficina, e como o
// Kali Gestão resolve cada um (funcionalidades confirmadas no ebook do produto).
const desafios = [
  {
    icon: Cog6ToothIcon,
    titulo: "Peça certa pro carro certo",
    problema:
      "Cada peça só serve em determinados modelos e anos de veículo — vender a peça errada gera devolução e cliente insatisfeito.",
    solucao:
      "Cadastro detalhado de produtos com curva ABC mostra o que gira rápido e evita erro na hora da venda.",
  },
  {
    icon: BanknotesIcon,
    titulo: "Fiado pra oficina parceira",
    problema:
      "Oficina e mecânico de confiança compram fiado e pagam só no fim do mês — sem controle, o caixa fica no vermelho.",
    solucao:
      "Contas a receber, boletos e conciliação bancária mostram exatamente quem deve o quê e quando cai no caixa.",
  },
  {
    icon: ChatBubbleLeftRightIcon,
    titulo: "Orçamento rápido pro mecânico",
    problema:
      "O mecânico quer saber o preço e se tem a peça na hora — se demorar, ele liga pro concorrente.",
    solucao:
      "Monte o orçamento e envie direto pelo WhatsApp, com condições personalizadas, sem digitar tudo de novo.",
  },
  {
    icon: ClipboardDocumentListIcon,
    titulo: "Cotação com fornecedor",
    problema:
      "Peça de reposição vem de fornecedores diferentes, com preço e prazo que mudam — comprar errado come a margem.",
    solucao:
      "Envie a cotação pra vários fornecedores de uma vez, compare o retorno e gere o pedido de compra automaticamente.",
  },
  {
    icon: ArrowPathIcon,
    titulo: "Garantia e troca de peça",
    problema:
      "Peça com defeito de fábrica precisa ser trocada ou devolvida — sem histórico, fica difícil provar a compra e o prazo de garantia.",
    solucao:
      "Histórico completo de vendas por cliente e produto facilita conferir a garantia e agilizar a troca.",
  },
  {
    icon: ScaleIcon,
    titulo: "Peça parada há meses no estoque",
    problema:
      "Capital empatado em peça que não vende há meses pesa no caixa e ocupa espaço que podia ter produto de giro rápido.",
    solucao:
      "Curva ABC e relatórios por categoria mostram o que está parado, ajudando a decidir liquidar ou não repor.",
  },
];

// Capacidades reais do sistema — sem número inventado, só o que o Kali de fato faz.
const capacidades = [
  {
    icon: Cog6ToothIcon,
    titulo: "Catálogo por veículo",
    texto: "Cadastro detalhado evita vender peça errada pro modelo e ano do carro.",
  },
  {
    icon: ChatBubbleLeftRightIcon,
    titulo: "Orçamento no WhatsApp",
    texto: "Monte e envie o orçamento pro mecânico sem digitar tudo de novo.",
  },
  {
    icon: BanknotesIcon,
    titulo: "Fiado sob controle",
    texto: "Contas a receber e conciliação bancária mostram quem deve o quê.",
  },
];

// Categorias de produto que uma loja de autopeças precisa organizar no mesmo estoque.
const categorias = [
  { icon: Cog6ToothIconSolid, label: "Peças de motor" },
  { icon: WrenchScrewdriverIcon, label: "Suspensão e freios" },
  { icon: BoltIcon, label: "Elétrica automotiva" },
  { icon: BeakerIcon, label: "Filtros e óleos" },
  { icon: SpeakerWaveIcon, label: "Acessórios e som" },
  { icon: WrenchIcon, label: "Ferramentas de oficina" },
];

const funcionalidades = [
  {
    icon: ChatBubbleLeftRightIcon,
    eyebrow: "Vendas",
    titulo: "Orçamento rápido pro mecânico",
    texto: "Monte e envie orçamentos pelo WhatsApp em poucos cliques, sem perder venda pra demora.",
  },
  {
    icon: BanknotesIcon,
    eyebrow: "Financeiro",
    titulo: "Fiado e boletos sob controle",
    texto: "Contas a receber e conciliação bancária mostram quem deve o quê, sem depender de planilha.",
  },
  {
    icon: ClipboardDocumentListIcon,
    eyebrow: "Compras",
    titulo: "Cotações com fornecedores",
    texto: "Compare preço e prazo de vários fornecedores antes de fechar o pedido de compra.",
  },
  {
    icon: ScaleIcon,
    eyebrow: "Estoque",
    titulo: "Curva ABC de estoque parado",
    texto: "Saiba o que gira rápido e o que está parado, pra decidir compra e liquidação com dados.",
  },
];

const documentosFiscais = ["NF-e", "NFC-e", "NFS-e", "CT-e", "CT-e OS", "MDF-e"];

const controleTributario = [
  { label: "Sped Fiscal", shade: "bg-kali-blue-700" },
  { label: "Bloco H", shade: "bg-kali-blue-600" },
  { label: "Bloco K", shade: "bg-kali-blue-500" },
  { label: "EFD Contribuições", shade: "bg-kali-blue-400" },
];

useSeo({
  title: "Sistema para Loja de Autopeças e Oficinas | Kali Gestão",
  description:
    "Peça certa pro modelo certo, orçamento rápido pro mecânico, fiado sob controle e cotação com fornecedor — o Kali Gestão organiza a rotina da sua loja de autopeças.",
  image: imageBg,
  schema: {
    "@context": "https://schema.org",
    "@type": ["SoftwareApplication", "LocalBusiness"],
    name: "Kali Gestão para Autopeças",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    url: "https://www.kalisistemas.com.br/segmento/autopecas",
    description:
      "Sistema de gestão para lojas de autopeças e oficinas: catálogo por modelo/ano, orçamento pra mecânico, fiado e cotação com fornecedor.",
    offers: {
      "@type": "Offer",
      price: "0",
      priceCurrency: "BRL",
    },
  },
});
</script>

<template>
  <div class="bg-white">
    <SegmentHero
      title="Controle total da loja de autopeças, do balcão à oficina parceira"
      description="Peça certa pro modelo certo, orçamento rápido pro mecânico e fiado sob controle — o Kali Gestão organiza tudo isso em um só sistema."
      primary-label="Solicite uma demonstração gratuita"
      primary-href="/contato?type=autopecas"
      secondary-label="Ver outros segmentos"
      secondary-href="/segmentos"
      :background-image="imageBg"
      background-class="bg-top bg-cover bg-slate-900"
      overlay-variant="left"
    />

    <!-- Navegação fixa -->
    <section class="sticky top-16 z-40 border-b border-gray-100 shadow-sm backdrop-blur bg-white/95">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <nav
          class="flex overflow-x-auto relative gap-6 items-center h-16"
          aria-label="Navegação da página"
        >
          <a
            v-for="item in navLinks"
            :key="item.href"
            :href="item.href"
            class="menu-item-underline relative inline-flex items-center px-2 h-16 text-sm font-bold text-gray-700 hover:text-[#006086] transition-all duration-300 whitespace-nowrap"
            :class="{ 'text-[#006086] active': activeNav === item.href }"
            @click="(e) => handleNavClick(e, item.href)"
          >
            {{ item.label }}
          </a>
        </nav>
      </div>
    </section>

    <!-- Visão geral -->
    <section id="visao-geral" class="overflow-hidden relative py-16 bg-white scroll-mt-24 lg:min-h-[620px]">
      <div class="hidden absolute inset-y-0 right-0 w-1/2 lg:block">
        <img
          :src="visaoGeralImg"
          alt="Kali Gestão"
          class="object-cover object-center w-full h-full"
        />
      </div>
      <div class="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="lg:max-w-xl">
          <h2
            class="text-4xl sm:text-5xl font-black tracking-tight !leading-[1.25] pb-2 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Feito pra rotina de quem vende autopeças
          </h2>
          <p class="mt-6 text-lg leading-8 text-gray-600">
            Um catálogo gigante, com peça que só serve em modelo e ano
            específico de veículo, cliente que compra fiado e preço de
            fornecedor que muda toda semana. O Kali Gestão organiza estoque,
            orçamento pro mecânico, financeiro e a parte fiscal em um só
            lugar, 100% online.
          </p>
          <div class="grid gap-4 mt-10 sm:grid-cols-3 lg:max-w-none">
            <div
              v-for="cap in capacidades"
              :key="cap.titulo"
              v-reveal
              class="p-4 bg-[#e6f0f4] rounded-2xl"
            >
              <component :is="cap.icon" class="w-6 h-6 text-kali-blue-600" />
              <p class="mt-2 text-sm font-bold text-gray-900">{{ cap.titulo }}</p>
              <p class="mt-1 text-xs leading-relaxed text-gray-600">{{ cap.texto }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Desafios reais do segmento -->
    <section
      id="desafios"
      class="py-16 bg-white scroll-mt-24"
      style="background-image: repeating-linear-gradient(135deg, rgba(0,96,134,0.08) 0px, rgba(0,96,134,0.08) 10px, transparent 10px, transparent 20px);"
    >
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <span
            class="inline-flex rounded-full bg-[#e6f0f4] px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] text-kali-blue-700"
            >Rotina real da loja</span
          >
          <h2
            class="mt-4 text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Seis dores que todo dono de loja de autopeças conhece
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Não é genérico: é peça certa pro carro certo, é fiado pra oficina
            parceira, é orçamento que não pode demorar. Veja como o Kali
            Gestão resolve cada uma.
          </p>
        </div>

        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-3">
          <article
            v-for="d in desafios"
            :key="d.titulo"
            v-reveal
            class="p-7 bg-white rounded-3xl border border-white transition shadow-[0_20px_45px_-15px_rgba(0,50,58,0.35)] hover:-translate-y-1 hover:shadow-[0_28px_55px_-15px_rgba(0,50,58,0.42)]"
          >
            <component :is="d.icon" class="w-10 h-10 text-kali-blue-600" />
            <h3 class="mt-5 text-lg font-bold text-gray-900">{{ d.titulo }}</h3>
            <p class="mt-3 text-sm leading-relaxed text-gray-600">
              {{ d.problema }}
            </p>
            <div class="p-4 mt-4 rounded-xl bg-[#e6f0f4]">
              <p class="text-sm leading-relaxed text-kali-blue-900">
                <span class="font-bold">Como o Kali resolve:</span>
                {{ d.solucao }}
              </p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <!-- Categorias de produto -->
    <section id="categorias" class="py-16 bg-gray-100 scroll-mt-24">
      <div class="grid gap-12 items-center px-4 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:grid-cols-[1.3fr_1fr]">
        <div class="grid grid-cols-2 gap-x-6 gap-y-10 sm:grid-cols-3">
          <div v-for="c in categorias" :key="c.label" v-reveal class="flex flex-col items-center text-center group">
            <component
              :is="c.icon"
              class="w-11 h-11 text-kali-blue-600 transition-transform duration-200 ease-out group-hover:-translate-y-1"
            />
            <p
              class="mt-3 text-xs font-semibold text-gray-800 transition-colors duration-200 sm:text-sm group-hover:text-kali-blue-600"
            >
              {{ c.label }}
            </p>
          </div>
        </div>
        <div v-reveal>
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Um catálogo gigante, sem virar
            <span class="text-kali-blue-600">bagunça</span>
          </h2>
          <p class="mt-4 text-base leading-7 text-gray-600">
            Motor, suspensão, elétrica, filtro, som — cada categoria tem uma
            lógica diferente de compra, estoque e venda. O Kali organiza tudo
            no mesmo cadastro, com curva ABC pra você nunca perder o controle
            do que entra e sai.
          </p>
          <button
            type="button"
            class="inline-flex justify-center items-center px-6 py-3 mt-6 text-base font-semibold text-white rounded-full border border-transparent shadow-sm bg-kali-blue-500 hover:bg-kali-blue-600"
            @click="openConsultorModal"
          >
            Fale com um consultor
          </button>
        </div>
      </div>
    </section>

    <!-- Funcionalidades em destaque -->
    <section id="funcionalidades" class="py-16 bg-gradient-to-br from-white via-gray-50 to-[#e6f0f4] scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div v-reveal class="max-w-2xl">
          <h2
            class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b]"
          >
            Do balcão ao contador, tudo conectado
          </h2>
        </div>
        <div class="grid gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div v-for="f in funcionalidades" :key="f.titulo" v-reveal class="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm">
            <component :is="f.icon" class="w-9 h-9 text-kali-blue-600" />
            <p class="mt-4 text-xs font-bold tracking-wide text-kali-blue-600 uppercase">{{ f.eyebrow }}</p>
            <h3 class="mt-1 text-base font-bold text-gray-900">{{ f.titulo }}</h3>
            <p class="mt-2 text-sm leading-relaxed text-gray-600">{{ f.texto }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Reforma tributária -->
    <section class="px-4 py-16 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div v-reveal class="overflow-hidden relative bg-kali-blue-600 rounded-[3rem] shadow-xl">
        <div class="grid gap-10 items-center px-8 py-12 lg:grid-cols-2 lg:px-16 lg:py-16">
          <div>
            <h2 class="text-2xl font-black tracking-tight text-white sm:text-3xl lg:text-4xl">
              Reforma tributária: é hora de se preparar!
            </h2>
            <p class="mt-4 text-base leading-7 text-kali-blue-100 sm:text-lg">
              O Kali Gestão acompanha as mudanças da reforma tributária em
              tempo real, mantendo sua empresa em conformidade sem parar de
              vender.
            </p>
            <a
              href="/reforma-tributaria"
              class="inline-flex justify-center items-center px-8 py-3 mt-8 text-sm font-bold tracking-wide text-kali-blue-700 uppercase bg-white rounded-full shadow-sm transition hover:bg-kali-blue-50"
            >
              Saiba mais
            </a>
          </div>
          <div class="hidden relative lg:block lg:h-80">
            <img
              :src="gestaoTributaria"
              alt="Especialistas em conformidade fiscal"
              class="object-cover absolute inset-0 w-4/5 h-full rounded-3xl shadow-lg"
            />
            <div class="flex absolute -top-6 left-4 justify-center items-center w-16 h-16 rounded-2xl shadow-lg bg-kali-gold">
              <DocumentCheckIcon class="w-8 h-8 text-white" />
            </div>
            <div class="flex absolute -bottom-6 right-4 justify-center items-center w-20 h-20 bg-cyan-400 rounded-2xl shadow-lg">
              <ScaleIcon class="w-9 h-9 text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Fiscal -->
    <section id="fiscal" class="py-16 bg-white scroll-mt-24">
      <div class="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div class="grid gap-12 lg:grid-cols-2">
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Documentos fiscais
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Emita documentos fiscais de forma descomplicada, vinculada
              automaticamente ao site da Receita Federal.
            </p>
            <div class="grid grid-cols-3 gap-3">
              <div
                v-for="d in documentosFiscais"
                :key="d"
                class="flex justify-center items-center py-4 text-lg font-bold rounded-lg bg-kali-blue-100 text-kali-blue-700"
              >
                {{ d }}
              </div>
            </div>
          </div>
          <div v-reveal>
            <h2 class="text-3xl sm:text-4xl font-black tracking-tight !leading-[1.25] pb-1 text-transparent bg-clip-text bg-gradient-to-r from-gray-900 via-[#006086] to-[#004d6b] mb-3">
              Fiscal e tributário sem dor de cabeça
            </h2>
            <p class="mb-6 text-lg leading-8 text-gray-600">
              Painel do Contador integrado, pra você acompanhar a parte fiscal
              da loja sem depender de planilha paralela.
            </p>
            <div class="grid grid-cols-2 gap-3">
              <div
                v-for="c in controleTributario"
                :key="c.label"
                class="flex items-end p-4 h-24 font-bold text-white rounded-lg"
                :class="c.shade"
              >
                {{ c.label }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="depoimentos" class="scroll-mt-24">
      <Clients />
    </section>

    <section id="duvidas" class="scroll-mt-24">
      <FaqPanel
        :items="[
          {
            question: 'Consigo montar orçamento rápido pro mecânico?',
            answer:
              'Sim. Você monta o orçamento e envia direto pelo WhatsApp, com condições personalizadas, sem digitar tudo de novo.',
          },
          {
            question: 'Dá pra vender fiado pra oficina parceira?',
            answer:
              'Sim. O sistema controla contas a receber, prazos e conciliação bancária, mostrando exatamente quem deve o quê.',
          },
          {
            question: 'O sistema ajuda a comparar fornecedor pra achar a peça mais barata?',
            answer:
              'Sim. Você envia a cotação pra vários fornecedores de uma vez e compara o retorno antes de fechar a compra.',
          },
          {
            question: 'Dá pra saber quais peças estão paradas no estoque?',
            answer:
              'Sim. A curva ABC e os relatórios por categoria mostram o que não gira, ajudando a decidir liquidar ou não repor.',
          },
        ]"
        description="Dúvidas sobre o Kali Gestão pra loja de autopeças? Veja as respostas e fale com nosso time."
        cta-label="Falar com Consultor"
        :cta-opens-modal="true"
      />
    </section>

    <section id="fale-conosco" class="scroll-mt-16">
      <CTA />
    </section>
  </div>
</template>

<style scoped>
.reveal-init {
  opacity: 0;
  transform: translateY(28px);
  transition: opacity 0.6s ease-out, transform 0.6s ease-out;
}

.reveal-visible {
  opacity: 1;
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .reveal-init {
    opacity: 1;
    transform: none;
    transition: none;
  }
}
</style>
